package br.gov.go.pge.permanenciaweb.site.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import br.gov.go.pge.permanenciaweb.site.IndustrialPalette
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.dom.ref
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.display
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.justifyContent
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.forms.ButtonSize
import com.varabyte.kobweb.silk.components.overlay.Overlay
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.asList
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.KeyboardEvent

/*
 * Destructive confirmations for the admin page. The card is an ARIA alert dialog rendered in its own
 * overlay, so it can stack over another modal without unmounting it. Cancel is the initial focus (the
 * least destructive action), Tab stays inside the dialog, and focus returns to the trigger on cancel.
 */

val ConfirmDialogStyle = CssStyle.base {
    Modifier
        .backgroundColor(IndustrialPalette.White)
        // 1px hairline borders stay in px per the units policy.
        .border(1.px, LineStyle.Solid, IndustrialPalette.Border)
        .borderRadius(0.375.cssRem)
        .padding(1.25.cssRem)
        .width(100.percent)
        .maxWidth(28.cssRem)
}

val ConfirmDialogTitleStyle = CssStyle.base {
    Modifier.margin(0.cssRem)
}

val ConfirmDialogSelfWarningStyle = CssStyle.base {
    Modifier
        .color(IndustrialPalette.Red)
        .fontSize(0.8125.cssRem)
        .fontWeight(FontWeight.SemiBold)
        .margin(0.cssRem)
}

val ConfirmDialogMessageStyle = CssStyle.base {
    Modifier.margin(0.cssRem)
}

val ConfirmDialogActionsStyle = CssStyle.base {
    Modifier
        .display(DisplayStyle.Flex)
        .justifyContent(JustifyContent.FlexEnd)
        .gap(0.5.cssRem)
        .margin(top = 1.cssRem)
}

/**
 * Modal confirmation for destructive admin actions.
 *
 * @param selfWarning red/emphasized line shown when the action affects the signed-in account. It is
 *   part of the dialog description, so screen readers announce it with the rest of the message.
 * @param dialogId stable hook for UI tests; title/description ids are derived from it.
 */
@Composable
internal fun ConfirmDialog(
    title: String,
    message: String,
    confirmLabel: String,
    onConfirm: () -> Unit,
    onCancel: () -> Unit,
    selfWarning: String? = null,
    dialogId: String = "confirm-dialog",
) {
    val titleId = "$dialogId-title"
    val descriptionId = "$dialogId-description"
    // Captured before the dialog mounts; restored once the caller clears its confirmation state.
    val previousFocus = remember { document.activeElement as? HTMLElement }

    fun cancel() {
        onCancel()
        // Let the caller's recomposition remove the dialog before returning focus.
        window.setTimeout({ previousFocus?.focus() }, 0)
    }

    DisposableEffect(dialogId) {
        val listener = EventListener { event ->
            val key = event as? KeyboardEvent ?: return@EventListener
            when (key.key) {
                "Escape" -> {
                    key.preventDefault()
                    cancel()
                }
                "Tab" -> {
                    // Query the live dialog instead of caching element references: the card is
                    // rerendered inside Silk's deferred overlay, so a remembered button could be
                    // a stale node by the time the key event arrives.
                    val dialog = document.getElementById(dialogId) as? HTMLElement
                        ?: return@EventListener
                    val focusables = dialog.querySelectorAll("button").asList()
                        .filterIsInstance<HTMLButtonElement>()
                        .filter { it.getAttribute("aria-disabled") != "true" }
                    if (focusables.isEmpty()) return@EventListener
                    val index = focusables.indexOfFirst { it == document.activeElement }
                    val next = if (key.shiftKey) {
                        if (index <= 0) focusables.lastIndex else index - 1
                    } else {
                        if (index == -1 || index == focusables.lastIndex) 0 else index + 1
                    }
                    key.preventDefault()
                    focusables[next].focus()
                }
            }
        }
        window.addEventListener("keydown", listener)
        onDispose { window.removeEventListener("keydown", listener) }
    }

    Overlay(
        modifier = Modifier.styleModifier { property("z-index", "100") },
        contentAlignment = Alignment.Center,
    ) {
        Box(Modifier.fillMaxSize().onClick { cancel() })

        Column(
            modifier = ConfirmDialogStyle.toModifier()
                .then(Modifier.id(dialogId))
                .attrsModifier {
                    attr("role", "alertdialog")
                    attr("aria-modal", "true")
                    attr("aria-labelledby", titleId)
                    attr("aria-describedby", descriptionId)
                },
        ) {
            H3(attrs = ConfirmDialogTitleStyle.toAttrs { attr("id", titleId) }) { Text(title) }

            Column(
                modifier = Modifier.gap(0.5.cssRem).margin(top = 0.75.cssRem)
                    .attrsModifier { attr("id", descriptionId) },
            ) {
                if (selfWarning != null) {
                    P(attrs = ConfirmDialogSelfWarningStyle.toAttrs()) { Text(selfWarning) }
                }
                P(attrs = ConfirmDialogMessageStyle.toAttrs()) { Text(message) }
            }

            Row(modifier = ConfirmDialogActionsStyle.toModifier()) {
                Button(
                    onClick = { cancel() },
                    variant = DefaultButtonVariant,
                    size = ButtonSize.SM,
                    ref = ref { element -> element.focus() },
                ) {
                    Text("Cancelar")
                }
                Button(
                    onClick = { onConfirm() },
                    variant = DangerButtonVariant,
                    size = ButtonSize.SM,
                ) {
                    Text(confirmLabel)
                }
            }
        }
    }
}
